import { Box, Typography, useTheme } from '@mui/material'
import { type KeyboardEvent, type PointerEvent, useEffect, useMemo, useRef, useState } from 'react'
import { useLocale } from 'react-admin'

/**
 * One series over time: a 2px line over a light wash, hairline gridlines, a
 * crosshair that snaps to the nearest day with the value in a tooltip — by
 * pointer and by keyboard (focus, then ←/→). Colours come from the app theme,
 * so it follows light and dark.
 */
export interface Point {
  date: string
  value: number
}

const HEIGHT = 220
const PAD = { top: 12, right: 16, bottom: 28, left: 48 }

/** 0 / a round step / … up to at least `max`, about four ticks. */
const ticks = (max: number) => {
  if (max <= 0) return [0]
  const raw = max / 4
  const magnitude = 10 ** Math.floor(Math.log10(raw))
  const step = [1, 2, 2.5, 5, 10].map((m) => m * magnitude).find((s) => s >= raw) ?? raw
  const out: number[] = []
  for (let v = 0; v <= max + step * 0.001; v += step) out.push(v)
  if (out[out.length - 1] < max) out.push(out[out.length - 1] + step)
  return out
}

export const ViewsChart = ({ points, label }: { points: Point[]; label: string }) => {
  const theme = useTheme()
  const locale = useLocale()
  const box = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(800)
  const [active, setActive] = useState<number | null>(null)

  useEffect(() => {
    if (!box.current) return
    const observer = new ResizeObserver(([entry]) =>
      setWidth(Math.max(320, entry.contentRect.width))
    )
    observer.observe(box.current)
    return () => observer.disconnect()
  }, [])

  const number = useMemo(() => new Intl.NumberFormat(locale), [locale])
  const day = useMemo(
    () => new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', year: 'numeric' }),
    [locale]
  )
  const month = useMemo(() => new Intl.DateTimeFormat(locale, { month: 'short' }), [locale])

  const yTicks = ticks(Math.max(0, ...points.map((p) => p.value)))
  const yMax = yTicks[yTicks.length - 1] || 1
  const innerW = width - PAD.left - PAD.right
  const innerH = HEIGHT - PAD.top - PAD.bottom
  const x = (i: number) =>
    PAD.left + (points.length > 1 ? (i / (points.length - 1)) * innerW : innerW / 2)
  const y = (v: number) => PAD.top + innerH - (v / yMax) * innerH

  const line = points
    .map((p, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(p.value).toFixed(1)}`)
    .join('')
  const area = points.length
    ? `${line}L${x(points.length - 1).toFixed(1)},${y(0)}L${x(0).toFixed(1)},${y(0)}Z`
    : ''
  // Month starts as x labels, January as its year; the first day too, unless a
  // month start follows too close to fit both.
  const starts = points
    .map((p, i) => ({ i, d: new Date(p.date) }))
    .filter(({ d }, k) => k === 0 || d.getUTCDate() === 1)
  const monthTicks =
    starts.length > 1 && starts[0].d.getUTCDate() !== 1 && x(starts[1].i) - x(0) < 48
      ? starts.slice(1)
      : starts
  const tickLabel = (d: Date) =>
    d.getUTCMonth() === 0 && d.getUTCDate() === 1 ? String(d.getUTCFullYear()) : month.format(d)

  const series = theme.palette.primary.main
  const ink = theme.palette.text.secondary
  const grid = theme.palette.divider
  const surface = theme.palette.background.paper

  const nearest = (clientX: number) => {
    const rect = box.current?.getBoundingClientRect()
    if (!rect || points.length === 0) return null
    const ratio = (clientX - rect.left - PAD.left) / innerW
    return Math.min(points.length - 1, Math.max(0, Math.round(ratio * (points.length - 1))))
  }
  const onMove = (e: PointerEvent) => setActive(nearest(e.clientX))
  const onKey = (e: KeyboardEvent) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return
    e.preventDefault()
    setActive((a) => {
      const from = a ?? points.length - 1
      return Math.min(points.length - 1, Math.max(0, from + (e.key === 'ArrowLeft' ? -1 : 1)))
    })
  }

  const current = active !== null ? points[active] : null

  return (
    <Box ref={box} sx={{ position: 'relative', width: '100%' }}>
      <svg
        width={width}
        height={HEIGHT}
        role='img'
        aria-label={label}
        tabIndex={0}
        onPointerMove={onMove}
        onPointerLeave={() => setActive(null)}
        onFocus={() => setActive(points.length - 1)}
        onBlur={() => setActive(null)}
        onKeyDown={onKey}
        style={{ display: 'block', outline: 'none', touchAction: 'pan-y' }}
        data-testid='views-chart'
      >
        {yTicks.map((t) => (
          <g key={t}>
            <line
              x1={PAD.left}
              x2={width - PAD.right}
              y1={y(t)}
              y2={y(t)}
              stroke={grid}
              strokeWidth={1}
            />
            <text
              x={PAD.left - 8}
              y={y(t)}
              dy='0.32em'
              textAnchor='end'
              fontSize={12}
              fill={ink}
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {number.format(t)}
            </text>
          </g>
        ))}
        {monthTicks.map(({ i, d }) => (
          <text
            key={i}
            x={x(i)}
            y={HEIGHT - 8}
            textAnchor={i === 0 ? 'start' : 'middle'}
            fontSize={12}
            fill={ink}
          >
            {tickLabel(d)}
          </text>
        ))}
        <path d={area} fill={series} fillOpacity={0.1} />
        <path
          d={line}
          fill='none'
          stroke={series}
          strokeWidth={2}
          strokeLinejoin='round'
          strokeLinecap='round'
        />
        {current && active !== null && (
          <g>
            <line
              x1={x(active)}
              x2={x(active)}
              y1={PAD.top}
              y2={PAD.top + innerH}
              stroke={ink}
              strokeWidth={1}
            />
            <circle
              cx={x(active)}
              cy={y(current.value)}
              r={4}
              fill={series}
              stroke={surface}
              strokeWidth={2}
            />
          </g>
        )}
      </svg>
      {current && active !== null && (
        <Box
          role='status'
          sx={{
            position: 'absolute',
            top: 4,
            left: Math.min(Math.max(x(active) + 12, 0), width - 170),
            pointerEvents: 'none',
            bgcolor: 'background.paper',
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            boxShadow: 2,
            px: 1.5,
            py: 1,
            minWidth: 150,
          }}
        >
          <Typography variant='subtitle1' sx={{ fontWeight: 600, lineHeight: 1.2 }}>
            {number.format(current.value)}
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            {day.format(new Date(current.date))}
          </Typography>
        </Box>
      )}
    </Box>
  )
}

export default ViewsChart
